import { useCallback } from 'react';

import { type TrackClickedConnectToSlackOptionParams, useTrackClickedConnectToSlackOption } from '@air/analytics';
import { Slack } from '@air/api';
import { type Board } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useSlackIntregrationProcessLocation } from '~/components/EditPrivateBoardModal/EditBoardModal/SlackIntegration/hooks/useSlackIntregrationProcessLocation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

type OnConnectSlackProps = {
  board: Pick<Board, 'id' | 'title'>;
  trackLocation: TrackClickedConnectToSlackOptionParams['trackLocation'];
};

export const useConnectToSlack = () => {
  const { currentWorkspaceRef } = useCurrentWorkspace();
  const { trackClickedConnectToSlackOption } = useTrackClickedConnectToSlackOption();
  const { setSlackIntegrationLocation } = useSlackIntregrationProcessLocation();

  const connectToSlack = useCallback(
    async ({ board, trackLocation }: OnConnectSlackProps) => {
      // Read from the ref at call time — `showConnectToBoard` closes over this callback when
      // the modal opens, so a render-scoped `currentWorkspace?.id` can still be undefined
      // while the picker later loads boards after the workspace resolves.
      const workspaceId = currentWorkspaceRef.current?.id;

      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      trackClickedConnectToSlackOption({
        boardTitle: board.title,
        trackLocation,
      });
      const windowOpenRef = window.open();
      try {
        const { url } = await Slack.getAuthUrlForBoard({ workspaceId, boardId: board.id });
        setSlackIntegrationLocation(trackLocation);
        if (windowOpenRef) {
          windowOpenRef.location.href = url;
        }
      } catch (error) {
        if (windowOpenRef) {
          windowOpenRef.close();
        }
        throw reportErrorToBugsnag({
          error,
          context: `Failed to get slack auth url for board`,
          metadata: { data: { board } },
        });
      }
    },
    [trackClickedConnectToSlackOption, setSlackIntegrationLocation, currentWorkspaceRef],
  );

  return { connectToSlack };
};
